import { forwardRef } from 'react';
import type { AlertProps } from './Alert.types';
import { Button } from '../Button/Button';

/**
 * Alert - Componente de alerta/diálogo del sistema de diseño Siesa
 *
 * Componente modal para confirmaciones con soporte para:
 * - Título y descripción
 * - Contenido personalizado (children)
 * - Botones de acción personalizados o por defecto
 * - Dark mode
 *
 * @example
 * ```tsx
 * <Alert
 *   title="Are you sure you want to refund this payment?"
 *   description="The refund will be reflected in 2 to 3 business days."
 *   onCancel={() => console.log('Cancelled')}
 *   onConfirm={() => console.log('Confirmed')}
 *   confirmText="Refund"
 * />
 * ```
 */
export const Alert = forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      title,
      description,
      children,
      actions,
      onCancel,
      onConfirm,
      cancelText = 'Cancel',
      confirmText = 'Confirm',
      className = '',
      ...props
    },
    ref
  ) => {
    // Si se proveen actions personalizadas, usarlas. Si no, usar botones por defecto
    const actionButtons = actions || (
      <>
        {onCancel && (
          <Button type="plain" size="base" onClick={onCancel}>
            {cancelText}
          </Button>
        )}
        {onConfirm && (
          <Button type="default" size="base" onClick={onConfirm}>
            {confirmText}
          </Button>
        )}
      </>
    );

    return (
      <div
        ref={ref}
        className={`
          w-[512px]
          bg-white
          dark:bg-dark-bg-primary
          border
          border-border-primary
          dark:border-dark-border-primary
          rounded-xl
          ${className}
        `.trim().replace(/\s+/g, ' ')}
        {...props}
      >
        {/* Content Container */}
        <div className="p-6 flex flex-col gap-4">
          {/* Text Section */}
          <div className="flex flex-col gap-3">
            {/* Title */}
            <p className="text-sm leading-5 font-bold text-content-primary dark:text-dark-content-primary">
              {title}
            </p>

            {/* Description (optional) */}
            {description && (
              <p className="text-sm leading-5 font-normal text-content-tertiary dark:text-content-tertiary">
                {description}
              </p>
            )}
          </div>

          {/* Body Content (optional) */}
          {children && (
            <div className="flex flex-col gap-3">
              {children}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-3 items-center justify-end">
            {actionButtons}
          </div>
        </div>
      </div>
    );
  }
);

Alert.displayName = 'Alert';
